import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../config/api';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2'; // Make sure this is already imported

const RemindersList = () => {
    const [reminders, setReminders] = useState([]);
    const [filteredReminders, setFilteredReminders] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDateFilter, setStartDateFilter] = useState('');
    const [endDateFilter, setEndDateFilter] = useState('');
    const [staffMap, setStaffMap] = useState({});
    const navigate = useNavigate();

    const formatDate = (date) => {
        const d = new Date(date);
        return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
    };

    // Fetch staff list and map by ID
    useEffect(() => {
        fetch(`https://pbhrm.com/api/staff`)
            .then((res) => res.json())
            .then((data) => {
                if (data.status === 1) {
                    const map = {};
                    data.data.forEach(staff => {
                        map[staff.id] = staff.name;
                    });
                    setStaffMap(map);
                }
            })
            .catch(err => console.error('Error fetching staff:', err));
    }, []);

    // Fetch reminders
    useEffect(() => {
        fetch(`${BASE_URL}reminders`)
            .then(res => res.json())
            .then(data => {
                if (data.status === 1) {
                    setReminders(data.data);
                    setFilteredReminders(data.data);
                } else {
                    alert('Failed to fetch reminders');
                }
            })
            .catch(err => console.error('Error fetching reminders:', err));
    }, []);

    const handleSearchChange = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        setFilteredReminders(
            reminders.filter(reminder =>
                reminder.title?.toLowerCase().includes(term)
            )
        );
    };

    const handleDateFilter = () => {
        let filtered = reminders;
        const start = startDateFilter ? new Date(startDateFilter).setHours(0, 0, 0, 0) : null;
        const end = endDateFilter ? new Date(endDateFilter).setHours(0, 0, 0, 0) : null;

        if (start) {
            filtered = filtered.filter(reminder =>
                new Date(reminder.time).setHours(0, 0, 0, 0) >= start
            );
        }
        if (end) {
            filtered = filtered.filter(reminder =>
                new Date(reminder.time).setHours(0, 0, 0, 0) <= end
            );
        }

        setFilteredReminders(filtered);
    };

    const handleAdd = () => navigate('/AddReminder');
    const handleEdit = (id) => navigate(`/EditReminder/${id}`);

    const handleDelete = (id) => {
      Swal.fire({
        title: "Are you sure?",
        text: "Do you really want to delete this reminder?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          fetch(`${BASE_URL}reminders/${id}`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
          })
            .then((res) => res.json())
            .then((data) => {
              if (data.status === 1) {
                const updated = reminders.filter((r) => r.id !== id);
                setReminders(updated);
                setFilteredReminders(updated);
                Swal.fire("Deleted!", "Reminder has been deleted.", "success");
              } else {
                Swal.fire("Error!", "Failed to delete reminder.", "error");
              }
            })
            .catch((err) => {
              console.error("Error deleting reminder:", err);
              Swal.fire(
                "Error!",
                "An error occurred. Please try again.",
                "error"
              );
            });
        }
      });
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text('Reminder List', 14, 10);
        const tableData = filteredReminders.map(reminder => [
            reminder.id,
            reminder.title || 'N/A',
            formatDate(reminder.time),
            staffMap[reminder.staff_id] || 'N/A'
        ]);

        doc.autoTable({
            head: [['ID', 'Title', 'Time', 'Staff']],
            body: tableData,
        });
        doc.save('reminder_list.pdf');
    };

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Reminder List</h3>
                        </div>
                    </div>
                </div>
            </div>

            <section className="section">
                <div className="row" id="basic-table">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between">
                                <div></div>
                                <div>
                                    <button className="btn btn-primary" onClick={handleAdd}>Add Reminder</button>
                                    <button className="btn btn-secondary ms-2" onClick={handleExportPDF}>Export to PDF</button>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <label className="form-label">Search by Title</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search title"
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Start Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={startDateFilter}
                                            onChange={e => setStartDateFilter(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">End Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={endDateFilter}
                                            onChange={e => setEndDateFilter(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-4 mt-2">
                                        <button className="btn btn-secondary mt-4" onClick={handleDateFilter}>Apply Date Filter</button>
                                    </div>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-lg">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Title</th>
                                                <th>Time</th>
                                                <th>Staff</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredReminders.map(reminder => (
                                                <tr key={reminder.id}>
                                                    <td>{reminder.id}</td>
                                                    <td>{reminder.title}</td>
                                                    <td>{formatDate(reminder.time)}</td>
                                                    <td>{staffMap[reminder.staff_id] || 'N/A'}</td>
                                                    <td className="text-end">
                                                        <button className="btn btn-success" onClick={() => handleEdit(reminder.id)}>Edit</button>
                                                        <button className="btn btn-danger ms-2" onClick={() => handleDelete(reminder.id)}>Delete</button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {filteredReminders.length === 0 && (
                                                <tr>
                                                    <td colSpan="5" className="text-center">No reminders found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default RemindersList;
